import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

// Layout + Components
import DefaultAuth from 'layouts/auth/Default';
import { HSeparator } from 'components/separator/Separator';

// Icons
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';

// Assets
import illustration from 'assets/img/auth/bg-auth.jpg';

// API + Utils
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';

function AcceptInvitation() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location?.search);
  const tokenFromUrl = query.get('token');
  const emailFromUrl = query.get('email');
  console.log(' AcceptInvitation ~ tokenFromUrl:', tokenFromUrl);
  // Chakra color mode styling
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'secondaryGray.600';
  const textColorBrand = useColorModeValue('brand.700', 'brand.400');
  const brandStars = useColorModeValue('red.500', 'red.400');

  // Password show/hide
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const [loading, setLoading] = useState(false);

  // Formik setup
  const validationSchema = Yup.object().shape({
    fullName: Yup.string().required('Full Name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    address: Yup.string().required('Address is required'),
    phone: Yup.string().matches(
      /^\(\d{3}\) \d{3}-\d{4}$/,
      'Phone number must be in format (XXX) XXX-XXXX',
    ),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      address: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      await acceptInvitation(values);
    },
  });

  // Submit handler
  const acceptInvitation = async (formValues) => {
    try {
      setLoading(true);
      const payload = {
        name: formValues.fullName,
        email: formValues.email,
        address: formValues.address,
        phone: formValues.phone,
        password: formValues.password,
        con_password: formValues.confirmPassword,
        invitation_token: tokenFromUrl,
      };

      // Call your "accept invitation" endpoint
      // Adjust the endpoint string to match your backend route
      await createAPIEndPoint('team/register').create(payload);

      toast.success('Invitation accepted successfully! You can now log in.');
      formik.resetForm();
      navigate('/auth/sign-in');
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          'Failed to accept invitation. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, ''); // Remove non-numeric characters
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return value;

    const formatted = !match[2]
      ? match[1]
      : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;

    return formatted.length > 14 ? formatted.slice(0, 14) : formatted; // Limit to 14 characters
  };
  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '90%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="fit-content"
        // maxH="90vh"
        alignItems="start"
        justifyContent="center"
        px={{ base: '25px', md: '30px' }}
        // mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
        p={6}
        borderRadius="lg"
        boxShadow="lg"
        bg="white"
        // overflowY="auto"
      >
        <Box me="auto">
          <Heading
            color={textColor}
            fontSize={{ base: '24px', md: '26px', lg: '28px' }}
            mb="10px"
            whiteSpace="normal"
            wordBreak="break-word"
          >
            Register for Your Dental 360 Pre-Auth Portal
          </Heading>

          <Text
            mb="20px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Please fill in the details to complete your registration.
          </Text>
        </Box>

        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '563.85px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
        >
          <Flex align="center" mb="20px">
            <HSeparator />
            <HSeparator />
          </Flex>

          <form
            onSubmit={formik.handleSubmit}
            // style={{
            //   maxHeight: '80vh',
            //   overflowY: 'auto',
            // }}
          >
            {/* Full Name */}
            <FormControl
              mb="20px"
              isInvalid={formik.touched.fullName && formik.errors.fullName}
            >
              {/* <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Full Name<Text color={brandStars}>*</Text>
              </FormLabel> */}
              <Input
                name="fullName"
                variant="auth"
                fontSize="sm"
                type="text"
                placeholder="Enter your full name"
                fontWeight="500"
                size="md"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                borderColor={
                  formik.errors.fullName && formik.touched.fullName
                    ? 'red.500'
                    : 'gray.200'
                }
              />
              {formik.touched.fullName && formik.errors.fullName && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.fullName}
                </Text>
              )}
            </FormControl>

            {/* Email Address */}
            <FormControl
              mb="20px"
              isInvalid={formik.touched.email && formik.errors.email}
            >
              {/* <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                E-Mail Address<Text color={brandStars}>*</Text>
              </FormLabel> */}
              <Input
                name="email"
                variant="auth"
                fontSize="sm"
                type="email"
                placeholder="Enter your email"
                fontWeight="500"
                size="md"
                disabled={emailFromUrl}
                value={emailFromUrl ? emailFromUrl : formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                borderColor={
                  formik.errors.email && formik.touched.email
                    ? 'red.500'
                    : 'gray.200'
                }
              />
              {formik.touched.email && formik.errors.email && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.email}
                </Text>
              )}
            </FormControl>

            {/* Address */}
            <FormControl
              mb="20px"
              isInvalid={formik.touched.address && formik.errors.address}
            >
              {/* <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Address<Text color={brandStars}>*</Text>
              </FormLabel> */}
              <Input
                name="address"
                variant="auth"
                fontSize="sm"
                type="text"
                placeholder="Enter your address"
                fontWeight="500"
                size="md"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                borderColor={
                  formik.errors.address && formik.touched.address
                    ? 'red.500'
                    : 'gray.200'
                }
              />
              {formik.touched.address && formik.errors.address && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.address}
                </Text>
              )}
            </FormControl>

            {/* Phone */}
            <FormControl
              mb="20px"
              isInvalid={formik.touched.phone && formik.errors.phone}
            >
              {/* <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Phone<Text color={brandStars}>*</Text>
              </FormLabel> */}
              <Input
                name="phone"
                variant="auth"
                fontSize="sm"
                type="text"
                placeholder="(XXX) XXX-XXXX"
                fontWeight="500"
                size="md"
                maxLength={14}
                value={formik.values.phone}
                onChange={(e) =>
                  formik.handleChange({
                    target: {
                      name: 'phone',
                      value: formatPhoneNumber(e.target.value),
                    },
                  })
                }
                onBlur={formik.handleBlur}
                borderColor={
                  formik.errors.phone && formik.touched.phone
                    ? 'red.500'
                    : 'gray.200'
                }
              />
              {formik.touched.phone && formik.errors.phone && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.phone}
                </Text>
              )}
            </FormControl>

            {/* Password */}
            <FormControl
              mb="20px"
              isInvalid={formik.touched.password && formik.errors.password}
            >
              {/* <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Password<Text color={brandStars}>*</Text>
              </FormLabel> */}
              <InputGroup size="md">
                <Input
                  name="password"
                  fontSize="sm"
                  placeholder="Enter your password"
                  size="md"
                  type={showPassword ? 'text' : 'password'}
                  variant="auth"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  borderColor={
                    formik.errors.password && formik.touched.password
                      ? 'red.500'
                      : 'gray.200'
                  }
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={toggleShowPassword}
                  />
                </InputRightElement>
              </InputGroup>
              {formik.touched.password && formik.errors.password && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.password}
                </Text>
              )}
            </FormControl>

            {/* Confirm Password */}
            <FormControl
              mb="20px"
              isInvalid={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
            >
              {/* <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Confirm Password<Text color={brandStars}>*</Text>
              </FormLabel> */}
              <InputGroup size="md">
                <Input
                  name="confirmPassword"
                  fontSize="sm"
                  placeholder="Confirm your password"
                  size="md"
                  type={showConfirmPassword ? 'text' : 'password'}
                  variant="auth"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  borderColor={
                    formik.errors.confirmPassword &&
                    formik.touched.confirmPassword
                      ? 'red.500'
                      : 'gray.200'
                  }
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={
                      showConfirmPassword
                        ? RiEyeCloseLine
                        : MdOutlineRemoveRedEye
                    }
                    onClick={toggleShowConfirmPassword}
                  />
                </InputRightElement>
              </InputGroup>
              {formik.touched.confirmPassword &&
                formik.errors.confirmPassword && (
                  <Text color="red.500" fontSize="sm">
                    {formik.errors.confirmPassword}
                  </Text>
                )}
            </FormControl>

            {/* Submit Button */}
            <Button
              type="submit"
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="40px"
              mb="8px"
              isLoading={loading}
            >
              {loading ? 'Registering...' : 'Register'}
            </Button>
          </form>

          {/* Already have an account? */}
          <Flex flexDirection="row" justifyContent="center" alignItems="center">
            <Text color={textColor} fontSize="sm" fontWeight="400" me="4px">
              Already have an account?
            </Text>
            <NavLink to="/auth/sign-in">
              <Text
                color={textColorBrand}
                as="span"
                fontSize="sm"
                fontWeight="500"
              >
                Login
              </Text>
            </NavLink>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default AcceptInvitation;
